import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

axios.defaults.baseURL = process.env.REACT_APP_API_URL;

const postToM3 = async (endpoint, payload, rejectWithValue) => {
  try {
    const { token, ...body } = payload || {};
    const response = await axios.post(`/shareAbhaDataM3/${endpoint}`, body, {
      headers: {
        "Content-Type": "application/json",
        token,
      },
    });
    if (response.data?.success === false) {
      return rejectWithValue(
        response.data?.result?.message ||
          response.data?.message ||
          "M3 request failed"
      );
    }
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
};

const createM3Thunk = (type, endpoint) =>
  createAsyncThunk(type, async (payload, { rejectWithValue }) =>
    postToM3(endpoint, payload, rejectWithValue)
  );

export const setBridgeUrlM3 = createM3Thunk(
  "shareAbhaDataM3/setBridgeUrlM3",
  "setBridgeUrl"
);

export const associateBridgeIdM3 = createM3Thunk(
  "shareAbhaDataM3/associateBridgeIdM3",
  "associateBridgeId"
);

export const getBridgeServicesM3 = createM3Thunk(
  "shareAbhaDataM3/getBridgeServicesM3",
  "bridge/services"
);

export const initiateConsentRequestM3 = createM3Thunk(
  "shareAbhaDataM3/initiateConsentRequestM3",
  "consent/init"
);

export const getConsentRequestStatusM3 = createM3Thunk(
  "shareAbhaDataM3/getConsentRequestStatusM3",
  "consent/status"
);

export const fetchConsentArtifactM3 = createM3Thunk(
  "shareAbhaDataM3/fetchConsentArtifactM3",
  "consent/fetch"
);

export const listConsentRequestsM3 = createM3Thunk(
  "shareAbhaDataM3/listConsentRequestsM3",
  "consent/list"
);

export const listConsentArtifactsM3 = createM3Thunk(
  "shareAbhaDataM3/listConsentArtifactsM3",
  "consent/artifacts"
);

export const requestHealthInformationM3 = createM3Thunk(
  "shareAbhaDataM3/requestHealthInformationM3",
  "health-information/request"
);

export const notifyHealthInformationM3 = createM3Thunk(
  "shareAbhaDataM3/notifyHealthInformationM3",
  "health-information/notify"
);

export const listHealthRecordsM3 = createM3Thunk(
  "shareAbhaDataM3/listHealthRecordsM3",
  "health-information/list"
);

export const getHealthRecordByIdM3 = createM3Thunk(
  "shareAbhaDataM3/getHealthRecordByIdM3",
  "health-information/view"
);

const initialState = {
  loading: false,
  error: null,
  lastMessage: "",
  lastResponse: null,
  consentRequests: [],
  consentArtifacts: [],
  healthRecords: [],
  healthRecordDetail: null,
  bridgeInfo: null,
  bridgeServices: [],
  bridgeServiceMatched: null,
  recommendedHiuServiceId: null,
};

const shareAbhaDataM3Slice = createSlice({
  name: "shareAbhaDataM3",
  initialState,
  reducers: {
    resetShareAbhaDataM3State: () => initialState,
    clearShareAbhaDataM3Error: (state) => {
      state.error = null;
    },
    clearShareAbhaDataM3PatientData: (state) => {
      state.error = null;
      state.lastMessage = "";
      state.lastResponse = null;
      state.consentRequests = [];
      state.consentArtifacts = [];
      state.healthRecords = [];
      state.healthRecordDetail = null;
    },
    clearShareAbhaDataM3HealthRecords: (state) => {
      state.healthRecords = [];
      state.healthRecordDetail = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(listConsentRequestsM3.fulfilled, (state, action) => {
      state.consentRequests = action.payload?.result?.list || [];
    });

    builder.addCase(listConsentArtifactsM3.fulfilled, (state, action) => {
      state.consentArtifacts = action.payload?.result?.list || [];
    });

    builder.addCase(listHealthRecordsM3.fulfilled, (state, action) => {
      state.healthRecords = action.payload?.result?.list || [];
    });

    builder.addCase(getHealthRecordByIdM3.fulfilled, (state, action) => {
      state.healthRecordDetail = action.payload?.result?.list || null;
    });

    builder.addCase(getBridgeServicesM3.fulfilled, (state, action) => {
      const list = action.payload?.result?.list || {};
      state.bridgeInfo = list.bridge || null;
      state.bridgeServices = Array.isArray(list.services) ? list.services : [];
      state.bridgeServiceMatched = list.matchedService || null;
      state.recommendedHiuServiceId = list.recommendedHiuServiceId || null;
    });

    builder.addMatcher(
      (action) =>
        action.type.startsWith("shareAbhaDataM3/") &&
        action.type.endsWith("/pending"),
      (state) => {
        state.loading = true;
        state.error = null;
      }
    );

    builder.addMatcher(
      (action) =>
        action.type.startsWith("shareAbhaDataM3/") &&
        action.type.endsWith("/rejected"),
      (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || "Request failed";
      }
    );

    builder.addMatcher(
      (action) =>
        action.type.startsWith("shareAbhaDataM3/") &&
        action.type.endsWith("/fulfilled"),
      (state, action) => {
        state.loading = false;
        state.error = null;
        state.lastResponse = action.payload;
        state.lastMessage = action.payload?.result?.message || "Success";
      }
    );
  },
});

export const {
  resetShareAbhaDataM3State,
  clearShareAbhaDataM3Error,
  clearShareAbhaDataM3PatientData,
  clearShareAbhaDataM3HealthRecords,
} =
  shareAbhaDataM3Slice.actions;

export default shareAbhaDataM3Slice.reducer;
